/**
 * Офлайн-очередь операций (ADR-0009, T-181): то, что сделали без связи, отправится позже.
 *
 * Хранится в localStorage браузера (в десктопном клиенте — в его профиле), отдельно для
 * каждого пользователя: чужие операции не уйдут под другим входом. Переживает перезапуск.
 * Каждая операция несёт `key` — он же заголовок Idempotency-Key (T-180): повтор после обрыва
 * связи вернёт прежний результат и не создаст вторую партию.
 */

export type OutboxKind = 'receive' | 'transition';

export interface OutboxOp {
  key: string;
  kind: OutboxKind;
  url: string;
  body: Record<string, unknown>;
  /** Для людей: «Клубника, 40 кг» или «SW-2026-000007 → Принято полностью». */
  label: string;
  createdAt: string;
  state: 'queued' | 'failed';
  error?: string | undefined;
  errorCode?: string | undefined;
}

const PREFIX = 'sww.outbox.v1.';

export function storageKey(userId: number): string {
  return `${PREFIX}${userId}`;
}

function isOp(v: unknown): v is OutboxOp {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.key === 'string' &&
    (o.kind === 'receive' || o.kind === 'transition') &&
    typeof o.url === 'string' &&
    typeof o.body === 'object' &&
    o.body !== null &&
    typeof o.label === 'string' &&
    (o.state === 'queued' || o.state === 'failed')
  );
}

/** Прочитать очередь; хранилище недоступно или испорчено — пустая (страница всё равно работает). */
export function loadOutbox(userId: number): OutboxOp[] {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isOp) : [];
  } catch {
    return [];
  }
}

export function saveOutbox(userId: number, ops: OutboxOp[]): boolean {
  try {
    if (ops.length) window.localStorage.setItem(storageKey(userId), JSON.stringify(ops));
    else window.localStorage.removeItem(storageKey(userId));
    return true;
  } catch {
    return false;
  }
}

export function newKey(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Что делать с ответом сервера на отправку из очереди. */
export type SendOutcome = 'sent' | 'stop' | 'failed';

/**
 * Нет сети, сервер упал (5xx), сессия кончилась (401) — остановиться и попробовать позже,
 * операция остаётся в очереди. Отказ по существу (4xx, 409) — в «Требует внимания».
 */
export function outcomeOf(status: number): SendOutcome {
  if (status >= 200 && status < 300) return 'sent';
  if (status === 0 || status === 401 || status === 408 || status === 429 || status >= 500) {
    return 'stop';
  }
  return 'failed';
}
